import React, { useId, useMemo } from 'react';

// Curva de área preenchendo a base do card — só desenhada quando existe
// histórico real (>= 2 pontos). Sem dado, o card fica só com ícone/label/
// número (nunca fabrica uma curva sem valores reais por trás).
const buildAreaPath = (values, width, height) => {
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const step = width / (values.length - 1);
    // Sem variação nenhuma (todo mundo com o mesmo valor, ex.: "alunos
    // ativos" ficou estável nos últimos 14 dias) — sem esse caso especial,
    // a normalização abaixo colapsava a linha pro canto de baixo do
    // gráfico (como se fosse o valor MÍNIMO possível), virando um traço de
    // 2px quase imperceptível. Uma série constante deveria aparecer como
    // uma faixa no meio do card, não sumir na borda.
    const points = range === 0
        ? values.map((v, i) => [i * step, height / 2])
        : values.map((v, i) => [i * step, height - ((v - min) / range) * (height - 4) - 2]);
    const line = points.map(([x, y]) => `${x},${y}`).join(' L');
    return `M${line} L${width},${height} L0,${height} Z`;
};

const AreaChart = ({ values, color }) => {
    const gradientId = useId();
    const width = 200;
    const height = 48;
    const path = useMemo(() => buildAreaPath(values, width, height), [values]);
    if (!path) return null;
    return (
        <svg
            width="100%"
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="none"
            style={{ position: 'absolute', bottom: 0, left: 0, borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' }}
        >
            <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.45" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.05" />
                </linearGradient>
            </defs>
            <path d={path} fill={`url(#${gradientId})`} />
        </svg>
    );
};

// Card de KPI reutilizável — ícone + label em caixa alta (cor de destaque
// própria do card) + número grande + curva de área real de fundo (quando
// há histórico). `history` nunca é inventado: cards sem série disponível
// (a maioria dos KPIs deste dashboard são contagens-snapshot, sem
// histórico guardado) simplesmente não desenham curva nenhuma.
export const StatCard = ({ label, value, secondary, icon, color = 'var(--primary-color)', history = [], loading = false, onClick }) => {
    const clickable = typeof onClick === 'function';
    return (
        <div
            className="card h-full relative overflow-hidden"
            style={{
                paddingBottom: history.length >= 2 ? '3.5rem' : undefined,
                cursor: clickable ? 'pointer' : undefined,
                transition: clickable ? 'box-shadow 0.15s, transform 0.15s' : undefined,
            }}
            onClick={clickable ? onClick : undefined}
            onMouseEnter={clickable ? (e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; } : undefined}
            onMouseLeave={clickable ? (e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; } : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
        >
            <div className="relative" style={{ zIndex: 1 }}>
                {icon && (
                    <i className={`pi ${icon} mb-2 block`} style={{ fontSize: '1.25rem', color, width: 'fit-content' }} />
                )}
                <div
                    className="text-xs font-bold mb-2"
                    style={{ color, letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                    {label}
                </div>
                <div className="text-3xl font-bold line-height-1" style={{ color: 'var(--text-color)' }}>
                    {loading ? '—' : value}
                </div>
                {secondary && (
                    <div className="text-color-secondary text-sm mt-2">{secondary}</div>
                )}
            </div>
            <AreaChart values={history} color={color} />
        </div>
    );
};
